import { useEffect, useRef, useState } from "react";
import { Check, ImagePlus, Loader2 } from "lucide-react";
import { field, label } from "@/components/signup/SignupShell";
import { mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { logoColor } from "@/lib/logoColor";

export const slugOf = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 30);

/** Screen 1 — business name (+ email + password when nobody is logged in). */
export function StepName({ form, setForm, loggedIn, host }) {
    const slug = slugOf(form.business_name) || "yourshop";
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
        <div className="space-y-4">
            <div>
                <label className={label} htmlFor="signup-name">Business name</label>
                <input id="signup-name" autoFocus className={`${field} mt-1`} value={form.business_name} onChange={set("business_name")}
                    placeholder="Sunset Graphics" data-testid="signup-name" autoComplete="organization" />
                <p className="mt-1.5 text-[14px] text-muted-ink" data-testid="signup-shop-line">
                    Your shop: <span className="font-semibold text-ink">{slug}.{host}</span>
                </p>
            </div>
            {!loggedIn && (
                <>
                    <div>
                        <label className={label} htmlFor="signup-email">Email</label>
                        <input id="signup-email" type="email" className={`${field} mt-1`} value={form.email} onChange={set("email")}
                            placeholder="you@yourshop.com" data-testid="signup-email" autoComplete="email" />
                    </div>
                    <div>
                        <label className={label} htmlFor="signup-password">Password</label>
                        <input id="signup-password" type="password" className={`${field} mt-1`} value={form.password} onChange={set("password")}
                            placeholder="8 characters or more" data-testid="signup-password" autoComplete="new-password" />
                    </div>
                </>
            )}
        </div>
    );
}

/** Screen 2 — one drop zone. Skipping it leaves a clean text logo made from the name. */
export function StepLogo({ logo, setLogo, onColor }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const pick = async (file) => {
        if (!file) return;
        setBusy(true); setErr("");
        try {
            const rec = await uploadArtwork(file);
            setLogo(rec.url);
            logoColor(file).then((c) => c && onColor?.(c)).catch(() => {});
        } catch { setErr("Upload failed. Try again."); }
        finally { setBusy(false); }
    };
    return (
        <div>
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
                className="flex min-h-[220px] flex-col items-center justify-center border border-dashed border-ink px-4 py-6 text-center"
                data-testid="signup-logo-drop">
                {logo
                    ? <img src={mediaUrl(logo)} alt="Your logo" className="max-h-[140px] max-w-full object-contain" data-testid="signup-logo-preview" />
                    : <ImagePlus size={36} className="text-muted-ink" />}
                <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="signup-logo-button"
                    className="mt-4 flex min-h-[44px] items-center gap-2 border border-ink bg-white px-4 text-[16px] font-semibold disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : null} {logo ? "Use a different file" : "Upload your logo"}
                </button>
                <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="signup-logo-input"
                    onChange={(e) => pick(e.target.files?.[0])} />
                <p className="mt-3 text-[14px] text-muted-ink">PNG or JPG. Skip it and we use your name in clean type.</p>
            </div>
            {err && <p className="mt-2 text-[14px] text-[#b3261e]" data-testid="signup-logo-error">{err}</p>}
        </div>
    );
}

const SWATCHES = ["#1a1a1a", "#1a56db", "#1e6b34", "#b3261e", "#7c3aed", "#c2410c", "#0f766e", "#be185d"];

/** Screen 3 — one swatch + hex, with a live product card so the color is seen on a button. */
export function StepColor({ color, setColor, shopName, logo }) {
    const valid = /^#[0-9a-fA-F]{6}$/.test(color);
    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
                <input type="color" value={valid ? color : "#1a1a1a"} onChange={(e) => setColor(e.target.value)} aria-label="Brand color"
                    className="h-[44px] w-[64px] cursor-pointer border border-ink bg-white p-0.5" data-testid="signup-color" />
                <input value={color} onChange={(e) => setColor(e.target.value.trim())} className={`${field} max-w-[160px] font-mono`}
                    data-testid="signup-color-hex" aria-label="Hex color" placeholder="#1a56db" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="signup-swatches">
                {SWATCHES.map((s) => (
                    <button key={s} type="button" onClick={() => setColor(s)} aria-label={`Use ${s}`} data-testid={`signup-swatch-${s.slice(1)}`}
                        className={`h-[44px] w-[44px] border ${color.toLowerCase() === s ? "border-2 border-ink" : "border-line"}`} style={{ background: s }} />
                ))}
            </div>
            <PreviewCard color={valid ? color : "#1a1a1a"} shopName={shopName} logo={logo} />
        </div>
    );
}

function PreviewCard({ color, shopName, logo }) {
    return (
        <div className="border border-ink" data-testid="signup-preview-card">
            <div className="flex min-h-[48px] items-center gap-2 border-b border-ink px-3">
                {logo && <img src={mediaUrl(logo)} alt="" className="h-8 w-8 object-contain" />}
                <span className="text-[16px] font-semibold">{shopName || "Your shop"}</span>
                <span className="ml-auto text-[14px] underline">Cart (0)</span>
            </div>
            <div className="grid grid-cols-2 gap-3 p-3">
                <div className="border border-ink">
                    <div className="aspect-square bg-[#f2f2f2]" />
                    <p className="px-2 pt-1 text-[14px] font-semibold">T-Shirts &amp; Hoodies</p>
                    <p className="px-2 pb-2 text-[13px] text-muted-ink">from $10</p>
                </div>
                <div className="flex flex-col justify-end gap-2">
                    <span className="inline-block w-fit border px-2 py-0.5 text-[12px] font-semibold" style={{ borderColor: color, color }}>Ready today</span>
                    <span className="flex min-h-[44px] items-center justify-center text-[15px] font-semibold text-[#fff]" style={{ background: color }}>Pay now</span>
                </div>
            </div>
        </div>
    );
}

/** Screen 4 — the free subdomain is ready; one optional own-domain field with the exact DNS record. */
export function StepDomain({ subdomain, host, domain, setDomain }) {
    return (
        <div className="space-y-5">
            <div className="flex items-center gap-2 border border-ink px-3 py-3" data-testid="signup-subdomain">
                <Check size={18} className="shrink-0" style={{ color: "var(--success, #1e6b34)" }} />
                <span className="text-[16px]"><span className="font-semibold">{subdomain}.{host}</span> — ready</span>
            </div>
            <div>
                <label className={label} htmlFor="signup-domain">Use my own domain (optional)</label>
                <input id="signup-domain" className={`${field} mt-1`} value={domain} onChange={(e) => setDomain(e.target.value)}
                    placeholder="shop.yourname.com" data-testid="signup-domain" autoComplete="off" />
                {domain.trim() && (
                    <div className="mt-3 border border-line px-3 py-3 text-[14px]" data-testid="signup-dns">
                        <p className="font-semibold">Add this record at your domain company:</p>
                        <p className="mt-1 font-mono">CNAME &nbsp; {domain.trim().toLowerCase().replace(/^https?:\/\//, "")} &nbsp;→&nbsp; shops.{host}</p>
                        <p className="mt-1 text-muted-ink">We check it in the background after you go live. It never holds up your shop.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Screen 5 — one sentence and Connect Stripe. The return lands on screen 6. */
export function StepStripe({ status }) {
    useEffect(() => { }, []);
    return (
        <div className="space-y-3 text-[16px]">
            <p>Customers pay you directly through Stripe.</p>
            <p className="text-muted-ink">Stripe opens in this tab and brings you straight back here when it is done. Card money goes to your bank, never through ours.</p>
            {status?.account_id && (
                <p className="border border-ink px-3 py-2 text-[15px]" data-testid="signup-stripe-started">
                    {status.charges_enabled ? "Stripe connected." : "You already started with Stripe — tap the button to finish."}
                </p>
            )}
        </div>
    );
}
